<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Slide-out sidebar for mobile -->
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog as="div" class="relative z-50 md:hidden" @close="sidebarOpen = false">
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel class="relative flex w-full max-w-xs flex-1">
              <button
                type="button"
                class="app-sidebar-edge-toggle app-sidebar-edge-toggle--mobile"
                @click="sidebarOpen = false"
              >
                <ChevronLeftIcon class="app-sidebar-edge-toggle__chevron" aria-hidden="true" />
                <span class="sr-only">Close sidebar</span>
              </button>
              <DashboardSidebarNav
                :items="sidebarItems"
                :help-items="helpItems"
                mobile
                @navigate="sidebarOpen = false"
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div
      class="app-sidebar-desktop hidden md:fixed md:inset-y-0 md:z-30 md:flex md:flex-col"
      :class="
        desktopSidebarOpen ? 'app-sidebar-desktop--expanded' : 'app-sidebar-desktop--collapsed'
      "
    >
      <div class="relative flex min-h-0 flex-1 flex-col border-r border-primaryDark/30 shadow-md">
        <DashboardSidebarNav
          :items="sidebarItems"
          :help-items="helpItems"
          :collapsed="!desktopSidebarOpen"
        />
        <button
          type="button"
          class="app-sidebar-edge-toggle app-sidebar-edge-toggle--desktop"
          :aria-label="desktopSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'"
          @click="toggleDesktopSidebar"
        >
          <ChevronLeftIcon
            v-if="desktopSidebarOpen"
            class="app-sidebar-edge-toggle__chevron"
            aria-hidden="true"
          />
          <ChevronRightIcon v-else class="app-sidebar-edge-toggle__chevron" aria-hidden="true" />
        </button>
      </div>
    </div>

    <!-- Main column (offset for sidebar on desktop) -->
    <div
      class="app-dashboard-main flex min-h-screen flex-col"
      :class="
        desktopSidebarOpen
          ? 'app-dashboard-main--sidebar-open'
          : 'app-dashboard-main--sidebar-closed'
      "
    >
      <header class="app-dashboard-header">
        <div class="app-dashboard-header__inner">
          <button type="button" class="app-dashboard-header__menu-btn" @click="sidebarOpen = true">
            <span class="sr-only">Open sidebar</span>
            <Bars3Icon class="h-5 w-5" aria-hidden="true" />
          </button>
          <RouterLink to="/campaigns" class="shrink-0 md:hidden">
            <img class="h-8 w-auto" src="../assets/givvy.png" alt="Givaroo logo" />
          </RouterLink>
          <h1 v-if="displayTitle" class="app-dashboard-header__title">
            {{ displayTitle }}
          </h1>
          <div v-else class="min-w-0 flex-1" />
          <div class="ml-auto shrink-0 overflow-visible">
            <NavMenu compact colored-header />
          </div>
        </div>
      </header>

      <main class="flex-1 px-4 py-5 sm:px-6">
        <slot></slot>
      </main>
    </div>

    <!-- Global celebration banner for newly earned achievements -->
    <AchievementBanner />
  </div>
</template>

<script>
import { computed, onBeforeMount, ref } from 'vue'
import { useRoute } from 'vue-router'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
import {
  MegaphoneIcon,
  CurrencyDollarIcon,
  Bars3Icon,
  UsersIcon,
  HomeIcon,
  TrophyIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/vue/20/solid'
import NavMenu from './NavMenu.vue'
import DashboardSidebarNav from './DashboardSidebarNav.vue'
import AchievementBanner from './AchievementBanner.vue'
import { useStore } from 'vuex'
import { useHelpWidgets, useSidebarHelpLauncher } from '@/composables/helpWidgets'

const SIDEBAR_ORDER = [
  '/dashboard',
  '/campaigns',
  '/donations',
  '/fundraisers',
  '/leaderboard',
  '/achievements',
  '/tasks',
]

export default {
  name: 'DashboardContainer',
  components: {
    NavMenu,
    DashboardSidebarNav,
    AchievementBanner,
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
    Bars3Icon,
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  props: { pageTitle: String },

  setup(props) {
    const store = useStore()
    const route = useRoute()
    const sidebarOpen = ref(false)
    const desktopSidebarOpen = ref(true)

    const toggleDesktopSidebar = () => {
      desktopSidebarOpen.value = !desktopSidebarOpen.value
    }

    // The assistant and bug report open from here instead of floating over the page.
    const { assistantOpen, bugReportOpen, canUseAssistant, canReportProblem } = useHelpWidgets()
    useSidebarHelpLauncher()
    const helpItems = computed(() => {
      const items = []
      if (canUseAssistant.value) {
        items.push({
          name: 'Ask Meredith',
          icon: SparklesIcon,
          open: () => {
            assistantOpen.value = true
          },
        })
      }
      if (canReportProblem.value) {
        items.push({
          name: 'Report a problem',
          icon: ChatBubbleLeftEllipsisIcon,
          open: () => {
            bugReportOpen.value = true
          },
        })
      }
      return items
    })

    const sidebarItemOptions = [
      {
        name: 'Dashboard',
        url: '/dashboard',
        icon: HomeIcon,
        allowedGroups: [
          'Canvassing Org Admin',
          'Super Canvassing Org Admin',
          'Canvassing Org Field Director',
          'Fundraiser',
          'NPO Admin',
          'Givaroo Staff',
        ],
      },
      {
        name: 'Campaigns',
        url: '/campaigns',
        icon: MegaphoneIcon,
        allowedGroups: [
          'Givaroo Staff',
          'NPO Admin',
          'Canvassing Org Admin',
          'Super Canvassing Org Admin',
          'Canvassing Org Field Director',
          'Fundraiser',
        ],
      },
      {
        name: 'Donations',
        url: '/donations',
        icon: CurrencyDollarIcon,
        allowedGroups: [
          'Givaroo Staff',
          'NPO Admin',
          'Fundraiser',
          'Canvassing Org Admin',
          'Super Canvassing Org Admin',
          'Canvassing Org Field Director',
        ],
      },
      {
        name: 'Fundraisers',
        url: '/fundraisers',
        icon: UsersIcon,
        allowedGroups: [
          'Canvassing Org Admin',
          'Super Canvassing Org Admin',
          'Canvassing Org Field Director',
          'Givaroo Staff',
        ],
      },
      {
        name: 'Leaderboard',
        url: '/leaderboard',
        icon: TrophyIcon,
        allowedGroups: [
          'Canvassing Org Admin',
          'Super Canvassing Org Admin',
          'Canvassing Org Field Director',
          'Fundraiser',
          'NPO Admin',
          'Givaroo Staff',
        ],
      },
      {
        name: 'Achievements',
        url: '/achievements',
        icon: SparklesIcon,
        allowedGroups: ['Fundraiser'],
      },
      {
        name: 'Tasks',
        url: '/tasks',
        icon: ClipboardDocumentListIcon,
        allowedGroups: [
          'Canvassing Org Admin',
          'Super Canvassing Org Admin',
          'Canvassing Org Field Director',
          'Fundraiser',
          'Givaroo Staff',
        ],
      },
    ]
    const userGroups = ref([])

    const sidebarItems = computed(() => {
      const matches = userGroups.value.flatMap((group) =>
        sidebarItemOptions.filter((item) => item.allowedGroups.includes(group.name)),
      )
      const seen = new Set()
      const deduped = matches.filter((item) => {
        if (seen.has(item.url)) return false
        seen.add(item.url)
        return true
      })
      return deduped.sort((a, b) => SIDEBAR_ORDER.indexOf(a.url) - SIDEBAR_ORDER.indexOf(b.url))
    })

    const isActiveRoute = (url) => {
      if (url === '/dashboard') {
        return route.path === '/dashboard'
      }
      return route.path === url || route.path.startsWith(`${url}/`)
    }

    const displayTitle = computed(() => {
      if (props.pageTitle) return props.pageTitle
      const match = sidebarItems.value.find((item) => isActiveRoute(item.url))
      return match?.name || ''
    })

    onBeforeMount(() => {
      if (store.getters.userGroups) {
        for (let i = 0; i < store.getters.userGroups.length; i++) {
          userGroups.value.push({ name: store.getters.userGroups[i], visible: false })
        }
      }
    })

    return {
      sidebarItems,
      helpItems,
      sidebarOpen,
      desktopSidebarOpen,
      toggleDesktopSidebar,
      displayTitle,
    }
  },
}
</script>
